import React, { useState } from 'react';
import { IRealEstateHasInventoryOption } from '../../../../../../Interfaces/IRealEstateHasInventoryOption';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { IRealEstate } from '../../../../../../Interfaces/IRealEstate';
import { IRealEstateUseType } from '../../../../../../Interfaces/IRealEstateUseType';
import { NumericFormat } from 'react-number-format';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PurchaseContractPersonOverview } from '../Person/PurchaseContractPersonOverview';
import { IPurchaseContractHasPerson } from '../../../../../../Interfaces/IPerson';

interface IProps {
  currentObject: IRealEstate;
  setCurrentObject: Function;
  realEstateUseTypeArray: IRealEstateUseType[];
  realEstateHasInventroyOptionArray: IRealEstateHasInventoryOption[];
  setHasWarningPrice: Function;
  marginTop?: number;
}

export const RealEstatePriceUseType: React.FC<IProps> = ({
  currentObject,
  setCurrentObject,
  realEstateHasInventroyOptionArray,
  realEstateUseTypeArray,
  setHasWarningPrice,
  marginTop,
}) => {
  const [hasWarning, setHasWarning] = useState(false);

  return (
    <>
      <Typography
        variant='h5'
        sx={{ mt: marginTop === undefined ? 2 : marginTop, mb: 1 }}
      >
        Nutzung
        <br />
      </Typography>

      <Box sx={{ mt: 5 }} />
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <NumericFormat
            value={currentObject.Price}
            suffix=' €'
            fixedDecimalScale
            thousandsGroupStyle='thousand'
            thousandSeparator='.'
            decimalSeparator=','
            fullWidth
            customInput={TextField}
            onValueChange={values => {
              if (values.floatValue !== undefined) {
                setHasWarning(false);
                setHasWarningPrice(false);
                setCurrentObject({
                  ...currentObject,
                  Price: values.floatValue,
                });
              } else {
                setHasWarning(true);
                setHasWarningPrice(true);
              }
            }}
            allowNegative={false}
            label='Kaufpreis'
            size='small'
            error={hasWarning}
          />
        </Grid>

        <Grid item sm={6}>
          <NumericFormat
            value={currentObject.PriceInventoryOption}
            suffix=' €'
            fixedDecimalScale
            thousandsGroupStyle='thousand'
            thousandSeparator='.'
            decimalSeparator=','
            fullWidth
            customInput={TextField}
            onValueChange={values => {
              if (values.floatValue !== undefined) {
                setCurrentObject({
                  ...currentObject,
                  PriceInventoryOption: values.floatValue,
                });
              } else {
                setCurrentObject({
                  ...currentObject,
                  PriceInventoryOption: null,
                });
              }
            }}
            allowNegative={false}
            label='Anteil der mitverkauften Gegenstände am Kaufpreis'
            size='small'
            disabled={realEstateHasInventroyOptionArray.length === 0}
          />
        </Grid>

        <Grid item sm={12}>
          <TextField
            label='Vermietung/Verpachtung'
            size='small'
            fullWidth
            required
            value={
              currentObject.idRealEstateUseType < 1
                ? ''
                : currentObject.idRealEstateUseType
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentObject({
                ...currentObject,
                idRealEstateUseType: Number(event.target.value),
              })
            }
            select
          >
            {realEstateUseTypeArray.map(x => (
              <MenuItem
                key={`idRealEstateUseType-${x.idRealEstateUseType}`}
                value={x.idRealEstateUseType}
              >
                {x.RealEstateUseType}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item sm={12}>
          <TextField
            label='Mängel oder Einschränkungen der Nutzbarkeit'
            size='small'
            fullWidth
            value={currentObject.Defect === null ? '' : currentObject.Defect}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentObject({
                ...currentObject,
                Defect: event.target.value === '' ? null : event.target.value,
              })
            }
            multiline
            rows={3}
          />
        </Grid>

        <Grid item sm={12}>
          <TextField
            label='Besonderheiten'
            size='small'
            fullWidth
            value={
              currentObject.Particularities === null
                ? ''
                : currentObject.Particularities
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentObject({
                ...currentObject,
                Particularities:
                  event.target.value === '' ? null : event.target.value,
              })
            }
            multiline
            rows={3}
          />
        </Grid>
        <Grid item sm={12} mt={6}>
          <FormControl component='fieldset'>
            <FormLabel component='legend'>Räumung notwendig?</FormLabel>
            <RadioGroup
              row
              value={currentObject.requiresClearance ? 'yes' : 'no'}
              onChange={e =>
                setCurrentObject({
                  ...currentObject,
                  requiresClearance: e.target.value === 'yes',
                  clearanceDate:
                    e.target.value === 'yes'
                      ? currentObject.clearanceDate
                      : null,
                })
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Ja' />
              <FormControlLabel value='no' control={<Radio />} label='Nein' />
            </RadioGroup>
          </FormControl>
        </Grid>

        {currentObject.requiresClearance && (
          <Grid item sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
              <DatePicker
                label='Räumung bis (Datum)'
                openTo='year'
                views={['year', 'month', 'day']}
                value={
                  currentObject.clearanceDate
                    ? dayjs(currentObject.clearanceDate)
                    : null
                }
                onChange={newValue =>
                  setCurrentObject({
                    ...currentObject,
                    clearanceDate: newValue
                      ? newValue.format('YYYY-MM-DD')
                      : '',
                  })
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        )}

        <Grid item sm={12} mt={6}>
          <FormControl component='fieldset'>
            <FormLabel component='legend'>Vermietet/Verpachtet?</FormLabel>
            <RadioGroup
              row
              value={currentObject.isRented ? 'yes' : 'no'}
              onChange={e =>
                setCurrentObject({
                  ...currentObject,
                  isRented: e.target.value === 'yes',
                })
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Ja' />
              <FormControlLabel value='no' control={<Radio />} label='Nein' />
            </RadioGroup>
          </FormControl>
        </Grid>
        {currentObject.isRented && (
          <Grid item sm={12}>
            <PurchaseContractPersonOverview
              handleBack={() => {}}
              handleNext={() => {}}
              idPruchaseContractRelationType={15}
              personArray={currentObject.rentedPersonArray}
              setPersonArray={(persons: IPurchaseContractHasPerson[]) => {
                setCurrentObject({
                  ...currentObject,
                  rentedPersonArray: persons,
                });
              }}
              title='Mieter/Pachter'
              text='Bitte geben Sie die Mieter/Pachter an.'
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
